import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useClientColyseus } from "../../Contexts/ClientColyseusContext";

const SpeedRoomGame = () => {
  const history = useHistory();
  const {
    room,
    isMaster,
    gameState,
    messages,
    sendChat,
    sessionId,
    courses,
    coursesLoaded,
  } = useClientColyseus();

  // --- Course Selection State ---
  const [selectedCourse, setSelectedCourse] = useState("");
  const [courseQuestions, setCourseQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Fetch questions only once for the selected course
  useEffect(() => {
    if (selectedCourse) {
      fetchCourseQuestions(selectedCourse);
    }
  }, [selectedCourse]);

  // Set selected course from context or room metadata ONCE on mount
  useEffect(() => {
    if (!room || !gameState) return;
    if (coursesLoaded) {
      const roomCourse = room?.metadata?.course || gameState?.course;
      if (roomCourse) {
        setSelectedCourse(roomCourse);
      } else if (courses && courses.length > 0 && !selectedCourse) {
        setSelectedCourse(courses[0].course_title);
      } else if (!roomCourse) {
        console.warn("No initial course found in room metadata or gameState. Host must select a course.");
      }
    }
  }, [coursesLoaded, courses, room, gameState]);

  // Fetch questions for selected course
  const fetchCourseQuestions = async (courseTitle) => {
    if (!courseTitle) return;

    setLoadingQuestions(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/quiz/${encodeURIComponent(
          courseTitle
        )}`
      );
      if (response.ok) {
        const questions = await response.json();
        setCourseQuestions(questions || []);
        console.log(
          `Loaded ${questions.length} questions for course: ${courseTitle}`
        );
      } else {
        console.error(
          `Failed to fetch questions for ${courseTitle}: ${response.status}`
        );
        setCourseQuestions([]);
      }
    } catch (error) {
      console.error(`Error fetching questions for ${courseTitle}:`, error);
      setCourseQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Update selected course in backend (if host)
  const handleCourseChange = (e) => {
    const courseTitle = e.target.value;
    setSelectedCourse(courseTitle);

    // Fetch questions for the selected course
    fetchCourseQuestions(courseTitle);

    // Find the course_id for the selected course
    const selectedCourseData = courses.find(
      (c) => c.course_title === courseTitle
    );
    const courseId = selectedCourseData ? selectedCourseData.course_id : "";

    // Send update to backend with both course title and ID
    if (isMaster && room) {
      room.send("change_course", {
        course: courseTitle,
        course_id: courseId,
      });
    }
  };
  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [selected, setSelected] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [timer, setTimer] = useState(0);
  const [answerFeedback, setAnswerFeedback] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  // Force re-render when gameState changes and check master status
  useEffect(() => {
    if (gameState) {
      console.log("GameState updated, forcing re-render");
      console.log(
        "Checking master status - sessionId:",
        sessionId,
        "master_client:",
        gameState.master_client
      );
      setForceRender((prev) => prev + 1);
    }
  }, [gameState, sessionId]);

  useEffect(() => {
    if (!room) return;
    // Timer logic
    let interval;
    if (gameState && gameState.timeLimit) {
      setTimer(gameState.timeLimit);
      interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [gameState && gameState.timeLimit]);

  // --- Lobby Countdown State ---
  const [lobbyCountdown, setLobbyCountdown] = useState(null);
  const requiredPlayers = 2; // You can make this dynamic if needed
  const countdownSeconds = 5;

  // Track current player count
  // Count only real/active players (exclude undefined/null/empty players)
  const playerCount =
    gameState && gameState.players
      ? Array.from(gameState.players.entries()).filter(([, p]) => p).length
      : 0;

  // Track if there are questions - allow start if course is selected (questions will be fetched on start)
  const hasQuestions = selectedCourse && selectedCourse.trim() !== "";
  const [lobbyError, setLobbyError] = useState("");

  useEffect(() => {
    // Only the host manages the countdown
    if (!isMaster || !room || !gameState) return;
    if (gameState.gameStarted) {
      setLobbyCountdown(null);
      return;
    }
    if (!hasQuestions) {
      setLobbyCountdown(null);
      setLobbyError("Please select a course first.");
      return;
    } else {
      setLobbyError("");
    }
    if (playerCount >= requiredPlayers) {
      if (lobbyCountdown === null) {
        setLobbyCountdown(countdownSeconds);
      }
    } else {
      setLobbyCountdown(null);
    }
  }, [playerCount, isMaster, room, gameState, hasQuestions]);

  // Remove auto-start countdown mechanism to prevent repeated requests
  // Game will only start when host explicitly clicks Start Game button

  useEffect(() => {
    if (!room) return;

    const onError = (message) => {
      console.error("Received error from server:", message);
      setLobbyError(
        `Server Error: ${message.error || JSON.stringify(message)}`
      );
    };

    room.onMessage("error", onError);

    return () => {
      // Make sure to remove the listener when the component unmounts
      if (room && typeof room.off === "function") {
        room.off("error", onError);
      }
    };
  }, [room]);

  if (!room || !gameState)
    return <div className="text-center p-12">Loading game...</div>;

  const questions = gameState.listQuestions || [];
  const currentQ = questions[currentQIdx];

  const handleStart = () => {
    if (!hasQuestions) {
      setLobbyError("Please select a course first.");
      return;
    }
    if (room && isMaster) {
      console.log("Sending start_game message to server...");
      room.send("start_game", {});
    } else {
      console.error(
        "Cannot start game: not the master client or room is not available."
      );
      alert(
        "Error: You are not the master of this room or the room is not available."
      );
    }
  };

  const handleAnswer = (ans) => {
    if (room) {
      room.send("answer", { answer: ans });
    }
    setSelected(ans);
    // The backend will determine if the answer is correct. Frontend shows neutral feedback.
    setShowResult(true);
    setAnswerFeedback("Answer submitted!");
    setTimeout(() => {
      setShowResult(false);
      setCurrentQIdx((prev) => prev + 1);
      setSelected("");
      setAnswerFeedback("");
    }, 1200);
  };

  const handleCopyId = () => {
    if (room && room.id) {
      navigator.clipboard.writeText(room.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }
  };
  const handleCopyLink = () => {
    if (room && room.id) {
      const link = `${window.location.origin}/multiplayer/room/${room.id}`;
      navigator.clipboard.writeText(link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleSendChat = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (chatInput.trim()) {
      sendChat(chatInput.trim());
      setChatInput("");
    }
  };

  // Game finished
  if (gameState.winner) {
    // Prepare answer breakdown
    const answers =
      gameState.answers && Array.isArray(gameState.answers)
        ? gameState.answers
        : [];
    const questionsList = gameState.listQuestions || [];
    // Try to get player name mapping
    const playerNames = gameState.players
      ? Object.fromEntries(
          Object.entries(gameState.players)
            .filter(([id, p]) => p)
            .map(([id, p]) => [id, p.name || `Player ${id.substring(0, 4)}`])
        )
      : {};
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <h2 className="text-2xl font-bold text-indigo-700 mb-4">
          Game Finished!
        </h2>
        <div className="mb-2">
          Winner: <span className="font-semibold">{gameState.winner}</span>
        </div>
        {/* Detailed answer breakdown */}
        <div className="w-full max-w-2xl mt-8 mb-6">
          <h3 className="text-lg font-semibold mb-2">Question Breakdown</h3>
          <table className="min-w-full bg-gray-50 rounded">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left">#</th>
                <th className="px-2 py-1 text-left">Question</th>
                <th className="px-2 py-1 text-left">Your Answer</th>
                <th className="px-2 py-1 text-left">Correct</th>
                <th className="px-2 py-1 text-left">Explanation</th>
              </tr>
            </thead>
            <tbody>
              {questionsList.map((q, idx) => {
                // Try to find this user's answer for this question
                let userAnswer = "-";
                let correct = false;
                let explanation = q.explanation || "";
                // If answers are per-user, per-question
                if (answers.length > 0) {
                  // Try to find answer for this user
                  const myId = room.sessionId;
                  const answerObj = answers.find(
                    (a) => a.playerId === myId && a.questionIdx === idx
                  );
                  if (answerObj) {
                    // If answer is an index or object, convert to readable text
                    if (typeof answerObj.answer === 'string') {
                      userAnswer = answerObj.answer;
                    } else if (typeof answerObj.answer === 'number' && q[`answer_title_${answerObj.answer}`]) {
                      userAnswer = q[`answer_title_${answerObj.answer}`];
                    } else if (typeof answerObj.answer === 'object' && answerObj.answer !== null) {
                      userAnswer = JSON.stringify(answerObj.answer);
                    } else {
                      userAnswer = String(answerObj.answer);
                    }
                    const correctKey = q.correct_answer.trim().toLowerCase();
                    const correctAnswerText = q[`answer_title_${correctKey}`];
                    correct = userAnswer === correctAnswerText;
                  }
                }
                return (
                  <tr key={idx} className="border-t">
                    <td className="px-2 py-1">{idx + 1}</td>
                    <td className="px-2 py-1">{q.question_title || "-"}</td>
                    <td
                      className={
                        "px-2 py-1 " +
                        (correct ? "text-green-700" : "text-red-700")
                      }
                    >
                      {userAnswer}
                    </td>
                    <td className="px-2 py-1">{q[`answer_title_${q.correct_answer.trim().toLowerCase()}`]}</td>
                    <td className="px-2 py-1">{explanation}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex gap-4 mt-4">
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded"
            onClick={() => window.location.reload()}
          >
            Replay
          </button>
      
        </div>
      </div>
    );
  }

  // --- Mode and Leaderboard Logic ---
  const mode =
    (gameState && gameState.mode) ||
    (room && room.metadata && room.metadata.mode) ||
    "competitive";

  // Debug logging
  // console.log("SpeedRoomGame - Current gameState:", gameState);
  // console.log("SpeedRoomGame - Players:", gameState?.players);
  // console.log("SpeedRoomGame - isMaster:", isMaster);
  // console.log("SpeedRoomGame - sessionId:", sessionId);
  // console.log("SpeedRoomGame - master_client:", gameState?.master_client);

  // Compute scores
  let leaderboard = [];
  let teamScore = 0;
  if (gameState && gameState.players) {
    // Convert MapSchema to array properly
    leaderboard = Array.from(gameState.players.entries())
      .filter(([id, player]) => player)
      .map(([id, player]) => {
        // Defensive: handle missing player or name
        let safeName = `Player ${id.substring(0, 4)}`;
        let safeScore = 0;
        if (player) {
          safeName = player.name || safeName;
          safeScore = typeof player.score === "number" ? player.score : 0;
        }
        return {
          id,
          name: safeName,
          score: safeScore,
        };
      })
      .sort((a, b) => b.score - a.score);
    teamScore = leaderboard.reduce((acc, p) => acc + p.score, 0);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Top Header Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium shadow-sm"
              onClick={() => history.push("/multiplayer")}
            >
              Back
            </button>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-semibold text-gray-800">Speed Duel</span>
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              Room: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{room.id}</span>
            </div>
            <button
              onClick={handleCopyId}
              className="px-3 py-1 bg-indigo-500 text-white rounded text-sm hover:bg-indigo-600 transition-colors"
            >
              {copied ? "✓" : "Copy ID"}
            </button>
            <button
              onClick={handleCopyLink}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
            >
              {copiedLink ? "Copied✓" : "Share"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 flex flex-col lg:flex-row gap-6">
        {/* Main Question Area - Takes up most space */}
        <div className="w-full lg:flex-1 mb-6 lg:mb-0">
          {/* Question Display */}
          {questions.length > 0 && currentQIdx < questions.length ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full font-bold text-lg">
                    Q{currentQIdx + 1}
                  </div>
                  <div className="sm:text-lg md: text-2xl font-bold text-gray-800">
                    Question {currentQIdx + 1} of {questions.length}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xl font-bold text-red-600">{timer}s</span>
                </div>
              </div>

              {/* Question Text */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-8">
                <div className="text-xl font-medium text-gray-800 leading-relaxed">
                  {currentQ.question_title}
                </div>
              </div>

              {/* Answer Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["a", "b", "c", "d"].map((k, index) => (
                  <button
                    key={k}
                    className={`p-6 rounded-xl border-2 text-left font-medium text-lg transition-all duration-200 transform hover:scale-[1.02] ${
                      selected === currentQ[`answer_title_${k}`]
                        ? "bg-indigo-100 border-indigo-500 text-indigo-800 shadow-lg"
                        : "bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50"
                    } ${showResult ? "cursor-not-allowed opacity-75" : "hover:shadow-md"}`}
                    onClick={() => handleAnswer(currentQ[`answer_title_${k}`])}
                    disabled={showResult}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        selected === currentQ[`answer_title_${k}`]
                          ? "bg-indigo-500 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span>{currentQ[`answer_title_${k}`]}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Answer Feedback */}
              {showResult && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="text-center text-green-700 font-bold text-lg">
                    {answerFeedback}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <div className="text-xl font-semibold text-gray-600 mb-2">
                  Get Ready for Questions!
                </div>
                <div className="text-gray-500">
                  Waiting for the game to start or questions to load...
                </div>
              </div>
            </div>
          )}

          {/* Lobby Controls */}
          {!gameState.gameStarted && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex flex-col gap-4">
                {isMaster && (
                  <div className="flex flex-col gap-3">
                    <label className="text-sm font-medium text-gray-700">
                      Select Course:
                    </label>
                    <select
                      value={selectedCourse}
                      onChange={handleCourseChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                    >
                      <option value="">Choose a course...</option>
                      {courses.map((course) => (
                        <option
                          key={course.course_id}
                          value={course.course_title}
                        >
                          {course.course_title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Course Questions Preview */}
                {selectedCourse && (
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="font-semibold text-gray-800">
                        Course: {selectedCourse}
                      </h3>
                      {loadingQuestions && (
                        <div className="text-sm text-blue-600">
                          Loading questions...
                        </div>
                      )}
                    </div>

                    {!loadingQuestions && courseQuestions.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-sm text-green-600 font-medium">
                          ✅ {courseQuestions.length} questions available
                        </div>
                        <div className="text-xs text-gray-600">
                          Sample: "
                          {courseQuestions[0]?.question_title?.substring(0, 80)}..."
                        </div>
                      </div>
                    )}

                    {!loadingQuestions &&
                      courseQuestions.length === 0 &&
                      selectedCourse && (
                        <div className="text-sm text-red-600">
                          ❌ No questions found for this course
                        </div>
                      )}
                  </div>
                )}

                {!isMaster && selectedCourse && (
                  <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md">
                    Course: <span className="font-medium">{selectedCourse}</span>
                  </div>
                )}

                {lobbyError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-red-600 font-medium">{lobbyError}</div>
                  </div>
                )}

                {isMaster && !gameState.gameStarted && (
                  <div className="flex flex-col gap-2">
                    <button
                      className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition-colors shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                      onClick={handleStart}
                      disabled={!hasQuestions || playerCount < requiredPlayers}
                    >
                      🚀 Start Game
                    </button>
                    {playerCount < requiredPlayers && (
                      <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-md text-center">
                        ⏳ Waiting for at least {requiredPlayers} players
                      </p>
                    )}
                  </div>
                )}

                {!isMaster && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-blue-700 font-medium">
                      👑 Waiting for host to start the game...
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Compact Chat and Leaderboard */}
        <div className="w-full lg:w-80 space-y-4">
          {/* Leaderboard */}
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-lg">🏆</span>
              Leaderboard
            </h3>
            {mode === "competitive" ? (
              <div className="space-y-2">
                {leaderboard
                  .filter((player) => player && player.name)
                  .slice(0, 5)
                  .map((player, idx) => (
                    <div key={player.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-400 text-yellow-800' : 
                          idx === 1 ? 'bg-gray-300 text-gray-700' : 
                          idx === 2 ? 'bg-orange-400 text-orange-800' : 
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {idx + 1}
                        </div>
                        <span className="text-sm font-medium truncate">{player.name}</span>
                      </div>
                      <span className="text-sm font-bold text-indigo-600">{player.score}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">{teamScore}</div>
                <div className="text-sm text-green-600">Team Score</div>
              </div>
            )}
            <div className="mt-3 text-xs text-gray-500 text-center">
              {playerCount} players online
            </div>
          </div>

          {/* Compact Chat */}
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-lg">💬</span>
              Chat
            </h3>
            <div
              className="overflow-y-auto mb-3 space-y-1"
              style={{ height: 200 }}
            >
              {messages.slice(-10).map((msg, idx) => {
                if (typeof msg === 'string') {
                  return (
                    <div key={idx} className="text-xs text-gray-600 p-2 bg-gray-50 rounded">
                      {msg}
                    </div>
                  );
                } else if (msg && typeof msg === 'object') {
                  const time = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '';
                  return (
                    <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                      <div className="font-medium text-gray-800">{msg.playerName || msg.sessionId || 'User'}</div>
                      <div className="text-gray-600">{msg.message}</div>
                      <div className="text-gray-400 text-xs">{time}</div>
                    </div>
                  );
                } else {
                  return null;
                }
              })}
            </div>
            <form onSubmit={handleSendChat} className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Type message..."
              />
              <button
                type="submit"
                className="bg-indigo-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-indigo-600 transition-colors"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedRoomGame;
